/* eslint-disable max-len */
import {
  useState, useMemo, useRef,
} from 'react';
import {
  FiCopy, FiCheck, FiEye, FiUpload, FiDownload,
} from 'react-icons/fi';
import useGetSelectedEvent from '../hooks/useGetSelectedEvent';
import useGetParams from '../hooks/useGetParams';
import {
  useGetAllTicketsQuery,
  useGetEventItemsExportQuery,
} from '../store';
import AdminSectionTitle from '../components/AdminSectionTitle';
import Select from '../components/atoms/Select/Select';
import Button from '../components/atoms/Button';
import { ItemExportRow } from '../store/apis/events';

// Labelary API configuration for ZPL preview
const LABELARY_API_URL = 'https://api.labelary.com/v1/printers/8dpmm/labels/2.0472452x5.9842552/0/';

// Convert image to ZPL GFA format
const imageToZplGfa = (
  imageData: ImageData,
  x: number,
  y: number,
): string => {
  const { width, height, data } = imageData;
  const bytesPerRow = Math.ceil(width / 8);
  const totalBytes = bytesPerRow * height;

  let hexData = '';

  for (let row = 0; row < height; row += 1) {
    let rowHex = '';
    for (let byteIndex = 0; byteIndex < bytesPerRow; byteIndex += 1) {
      let byte = 0;
      for (let bit = 0; bit < 8; bit += 1) {
        const pixelX = byteIndex * 8 + bit;
        if (pixelX < width) {
          const pixelIndex = (row * width + pixelX) * 4;
          // Get grayscale value (average of RGB)
          const gray = (data[pixelIndex] + data[pixelIndex + 1] + data[pixelIndex + 2]) / 3;
          // If pixel is dark (less than threshold), set bit to 1
          if (gray < 128) {
            // eslint-disable-next-line no-bitwise
            byte |= (1 << (7 - bit));
          }
        }
      }
      rowHex += byte.toString(16).toUpperCase().padStart(2, '0');
    }
    hexData += rowHex;
  }

  return `^FO${x},${y}^GFA,${totalBytes},${totalBytes},${bytesPerRow},${hexData}^FS`;
};

// Find and replace GFA graphic in template
const replaceGfaInTemplate = (template: string, newGfa: string): string => {
  // Match ^FO followed by coordinates, then ^GFA pattern until ^FS
  const gfaPattern = /\^FO\d+,\d+\^GFA,[^]*?\^FS/;
  if (gfaPattern.test(template)) {
    return template.replace(gfaPattern, newGfa);
  }
  // If no GFA found, add at the beginning after ^XA
  return template.replace(/\^XA\s*/, `^XA\n\n${newGfa}\n\n`);
};

const DEFAULT_ZPL_TEMPLATE = `
^XA

^FO10,50^GFA,60000,60000,50,,:::::::::::::::::::::::::::::::::::::N0783KF8,N07C7KFC,:::,::::U018,P078I03C,::::P078I07C,P078I0FC,N07MF8,:N07MF,N07LFC,P0F8,P078,:::,:U03C,:P07J03C,P078I03C,::P078I07C,P0F8001FC,N07MF8,N07MF,N07LFE,N07LF8,P078,::::P03,,:R07IF8,Q03JFC,Q0KFC,P01KFC,P03KFC,P07F,P07C,P078,P0F8,P0F,::P07,P078,:P038,P03C,P01F,P07KFC,:::P03KF8,,::::R0JF8,Q07JFC,P01KFC,P03KFC,:P07C3C1E,P0783C0F,P0701C078,P0F01C03C,:P0F01C01C,:P0F01E01C,P0701E01C,P0780E03C,P07C0F03C,P03E0F07C,P03F07DF8,P01F07FF8,Q0703FF,S01FE,T078,,P06,P07C,P07F,P07FE,P03FF8,Q0IF,Q01FFC,R07FF8,S0FFE,S01FF8,T07FC,U0FC,T01FC,T07FC,S03FF8,S0FFC,R07FF,Q01FF8,Q0FFE,P07FF,P07FC,P07E,P078,P04,,:R0JF8,Q07JFC,P01KFC,P03KFC,:P07C3C1E,P0783C0F,P0701C078,P0F01C03C,:P0F01C01C,:P0F01E01C,P0701E01C,P0780E03C,P07C0F03C,P03E0F07C,P03F07DF8,P01F07FF8,Q0703FF,S01FE,T078,,:::::::U038,U0F8,T03FC,T07FC,S01FF8,S07FF8,S0FFE,R03FF8,R0FFE,Q01FFC,Q07FF,P01FFC,P03FF8,P0FFE,O03FF8,O07FE,N01FFC,N01FF,N01FF8,N01FFC,O07FF,O01FFC,P0FFE,P03FF8,Q0FFE,Q07FF8,Q01FFC,R0IF,R03FFC,S0FFE,S07FF8,S01FF8,T07FC,T03FC,U0F8,U03,,::::::::::::::::::::::::::::::::::::::::::::::::::::::::R07JFE,Q07KFE3FC,Q0LFE3FE,P01LFE206,P01LFE306,P01LFE3FE,P01LFE0F8,P01F8,P01FL03,Q0FL03FE,:Q078,Q07KFE,P01LFE3FE,:P01LFE206,P01LFE306,P01LFE3FFC,P01LFE,,::R03FFE,Q01KF,Q07KF8,Q0LFC,Q0LFE,P01LFEhV041F,P01LFEhU01C7F8,P01F8I07FhU03C7FC,P01FJ03FhU038E0E,P01FJ03FhQ03I070C06,P01FJ03FhP01FI070C06,P01F8I03FhP0FFI070C06,P01LFEhO03F8I030C06,P01LFEhN01FEJ03860E,Q0LFEhN0FE6J01E73C,Q0LFChM01F86J01IF8,Q07KFChM01C06K07FF,Q03KFhN01F06,R07IF8hO0FE6,iL01FE,iM07F8M06,iN0FFJ0F806,iN01FI01FC06,N03F9LFEhQ03I038F06I0183F,N03F9LFEhU030786I0383F8,N03F9LFEhU0703C6I07073C,N03F9LFEhN03C03I0601E6I06061C,N03F9LFEhN0FF07I0700F6I0E0E0C,N03F9LFEhN0FF1FI03007EI0E0C0C,iK01C3FCI03C03EI0E0C0C,iK0181FJ01E00EI0E1C0C,iK0181EK06006I061C1C,iK01818R07F83C,R0F00FFhN01818R03F878,Q03F01FF8hM01818R01E07,Q07F03FFChM01818K03FE,Q0FF07FFEhM01JFJ0IF8,Q0FF0IFEhM01JFI01F9FC,P01FF0IFEhU03C01E,P01F81FC3EhU03I0EI07IFC,P01F01F83FhU07I06I07IFC,P01F03F81FhU07I06K0E,P01F03F01FhM01IFJ07I06K0C,P01F87F03FhM01IFCI03I0EK0C,P01IFE07EhM01IFEI03C01CK0C,P01IFE3FEhQ0EI01IFCK0C,Q0IFC3FEhQ07J0IFL0C,Q0IF83FChQ03K0F8L0C,Q07FF03FChQ03S0C,Q03FE03FhR03S0C,R078038hR03Q07IFC,iO07J0F806I07IFC,iO07I01FC06I07IFC,iN03EI03DE06,R0F00FFhN01IFCI030706,Q03F01FF8hM01IF8I070386,Q07F03FFChU0601C6J07FC,Q0FF07FFEhU0700E6I01IF,Q0FF0IFEhU03007EI03F1F8,P01FF0IFEhQ01I03C03EI07003C,P01F81FC3EhM018007I01E01EI06001C,P01F01F83FhM01C00FJ0E00EI0EI0C,P01F03F81FhM01E01EQ0EI0C,P01F03F01FhN0F07CQ0EI0C,P01F07F03FhN078FJ04M06001C,P01FDFE07EhN01FEJ078L078078,P01IFE3FEhO0F8J07F8K03IF8,Q0IFC3FEhO078K0FF8J01FFE,Q0IF83FChO038L0FF8J01F,Q07FF83FChO03EM0FF,Q03FE03FhN01JFM0FC,R0F8038hN01JFM01C,jH0E1F,jG03F3F8,iV07J07FF38,R07FE3EhP038K0F1F8I061E1C,Q03FFE3F8hN03FFJ01FBFCI0E0C0C,Q07FFE3FChN07FFCI039F0EI0C0C0C,Q0IFE3FEhN0F03EI030F06I0E0C0C,Q0IFE3FEhM01C00EI070606I06I0C,P01IFE3FEhM01C007I060606I07001C,P01F83E07EhM018003I070606I078038,P01F01E03FhM038003I03I0EI038038,P01F01E01FhM038003I03C01EL02,P01F01E01FhM038003I01C01C,P01F01E01FhM018007J0C018,P01F81E07FhM01C007,P01LFEhN0E01ER0406,P01LFEhN0FCFCR0E07,Q0LFEhN07FF8K07L0E07,Q0LFChN01FEK07FFK0402,Q07KFChU01IF8,Q01KFhV03E03C,iO03I03800E,iO03I07I06J0E00C,iO03I07I06I03F80C,iO03I07I06I07FC0C,R0F80FFhR03I03I06I060E0C,Q03F01FF8hQ03I03801EI0E070C,Q07F03FFChQ03I01F0FCI0C038C,Q0FF07FFEhQ03J0IF8I0E01CC,Q0FF0IFEhM01JFJ03FEJ0600FC,P01FF0IFEhM01JFQ07007C,P01F81FC3EiI03C03C,P01F01F83FhU06M01C01C,P01F03F81FhU07C,P01F03F01FhU03FC,P01F07F03FhV07FC,P01IFE07EhW07F8,P01IFE3FEhX07F8001F00C,Q0IFC3FEhY0FC003F80C,Q0IF83FCi0C007BC0C,Q07FF83FCiI060E0C,Q03FE03FhN01ET0E070C,R078038hN01FO07J0C038C,iL01O07J0E01EC,iS03IFEI0600FC,iS03IFEI07807C,iL01FEJ03807J03C03C,iL07FF8C003C07J01C01C,iL0FC7DC001F07,N01hW0E01F8I0787,N01hV01C00FJ01E7,N018hU01801FK0F7,N01ChU03803BK03F,N03ChU038033K01F,N03EhU038003L06,N03EJ03FhO018003,N07FJ0FFChN01C007,N03F8003FFEhN01C00E,N03F8007IF8hN0F03E,N01FC007IF8hN07FFCI03IFE,N01FC00JFChN03FFJ03IFE,O0FE01FFBFEhU03IFE,O07F01FE0FEhU038,O07F01FC07FhU018,O03F81F803F8hT01C,M0103F80F003F8hU0E,M0181FC07001FC,M0380FC06I0FC,M03C0FE02I0FE,M03C07F020407E,M03E07FI0C07F,M07E03F801E03F,M07F01F801E03F8,M03F01FC03F01FC,M03F80FE03F81FC,M01FC0FE03F80FE,M01FC07F01FC07E,N0FE07F01FC07F,N07E03F80FE03F,N07F01FC07E03F8,N03F01FC07F01F8,N03F80F803F81FC,N01F80F003F80FC,N01FC07001FC0FC,O0FC02I0FC07C,O0FE02040FE03C,O07FI0407F038,O03FI0E07F018,O03F800E03F818,O01F801F03F808,O01FC03F01FC,P0FE03F80FE,P0FF0FF00FE,P07JF007F,P03IFE007F,P03IFE003F8,P01IFC001FC,Q07FF8001FC,Q03FEJ0FC,R03K0FC,X078,X038,:X018,X01,,:::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::hY0F8,hX03FF,hX0IF8,hW01E03C,hW01C01C,hW03800E,hW038006,hW03I06,:hW038006,hW01800E,hW01C01C,hX0F07C,hX07FF8,hX03FE,,:::hW03IFE,:,::hX07C06,gH02gU0FE1E,gG01EgT01FF3E,gG0FEgT0183F8,g07FEgT0383E,Y01FFEgT03038,Y0IF8gT03018,X07FFCgU03018,W03FFEgV03018,W0IFEgV03IFE,V07FF8EgV03IFE,U03FFC0EgV03IFE,U03FE00E,U03FI0E,:U03FE00Eh0E,U03FFC0EgY07E,V07FF8EgX03FC,V01IFEgW01FE,W03FFEgW07FE,X07FFCgU03F86,Y0IF8gT03C06,Y03FFEgT03E06,g07FEgT01FC6,gG0FEgU03FE,gG01EgV07F,U038K02gV01FE,U03ChI03E,U03ChJ06,U03C,::U03Ch03IFE,:U03ChI03C,U03LFEgW078,U03LFEgV01E,U03LFEgV03C,U03LFEgV07,U03LFEgU01E,U03ChG03C,U03ChG0F,U03Ch01E,U03Ch03IFE,:U03C,::U03ChJ06,hW030306,::::U03LFEgT030306,:U03LFEgT03870E,U03LFEgT03IFE,g01FCgT03IFE,g03F8,g0FE,Y01FC,Y07FgV03,Y0FEgV03,X01F8gV03,X07FgW03,X0FEgW03,W03F8gW03IFE,W07FgX03IFE,V01FCgX03IFE,V03F8gX03,V0FEgY03,U01FCgY03,U03LFEgH0EQ03M07FC,U03LFEgG07FQ03M0FFE,U03LFEg03FF8W01C07,U03LFEY01IFCW038038,hH0JFCW030018,hG07JFCP03IFE0030018,h01KFCP03IFE0030018,h0KF7ES03C0030018,gY07JF83ES0780030018,gX03JFC03ER01EI03803,U03CJ01ET01JFE003ER03CI01E0F,U03C01C01ET0KFI03ER078J0FFE,U03C01C01ES07JF8I03EQ01EK03F8,U03C01C01ER03JFEJ03EQ03C,U03C01C01EQ01KFK03EQ0F,U03C01C01EQ0KF8K03EP01EK03,U03C01C01EP03JFCL03EP03IFE003,U03C01C01EO01JFEM03EP03IFE003,U03C01C01EO0KFN03EW03,U03C01C01EN03JF8N03EW03,U03C01C01EN07IFCO03FW03IF8,U03C01C01EN0IFEP01FW03IF8,U03C01C01EM01IFQ01FP030306003,U03C01C01EM01FFCQ01FP030306003,U03LFEM01FER01FP030306003,U03LFEM01FS01FP030306003,U03LFEM01FS01FP030306,U03LFEM01FR01FFP030306,gP01FQ01IFP030306,gP01FQ07FFEP03870E003IF8,gP01FQ07FFEP03IFE003IF8,gP01FN07007IFP03IFE,gP01FM03F003IF,gQ0F8K03FF003IF,V01800FEO0F8J03IF003IF8W0208,V07803FFO0F8I01JF003F9F8P0302J0E0E,V0F807FF8N0F8001KFK0F8P0F038001C0F,U01F807FFCN0F8S0F8P0E03C003803,U01F80F87CN0F8S0F8O01C01C0030018,U03E00F03EN0F8S0F8O03800E0030018,U03C01F01EN0F8S0F8O0380060030018,U03C01E01EN0F8S0F8O03I060030018,U03801E01EN0F8S0F8O03I060030018,U03803C00EN0F8S0F8O0380060038038,U03803C00EN0F8S0F8O01800E001C07,U03803C00EN0F8S0F8O01C01CI0FFE,U03C03C01EN07CS0F8P0F03CI07FC,U03C07801EN07CS07CP0IF8,U03E07801EN07CS07CP03FE,U01E0F803EN07CS07CgW0300F8,U01IF007CN07CS07CW03F8W0F01FE,V0IF01FCN07CS07CW0FFEV01F03FF,V07FE01F8N07CS07CV01E0FV01F07CF,V03FC01FO07IFQ07CO03IFE003803V03C07078,g01CO07OF8J07CO03IFE0030018U0380F038,gQ07UFCV030018U0380E038,gQ03TF7CV030018U0700E038,gQ03RF807CV030018U0700E038,gQ01PFCI07CR07I030018U0381C038,gR0OFK07CP0F9FC0038038U0381C038,U03CJ01EP07LFCK07EO01FDFC001C07V0383C038,U03C01C01EP0MF8K03EO01CF0E001FFEV03C7C078,U03C01C01EO01FFC003FL03EO038706I07FCV01FF81F,U03C01C01EO01FFI01EL03EO030606gH01FF01F,U03C01C01EO03FCJ0EL03EO030206gI07E01E,U03C01C01EO03F8J0EL03EO030206gL018,U03C01C01EO07FK04L03EO030206I0F8,U03C01C01EO07EK04L03EO038706001FE,U03C01C01EO07CK04L03EO03IFE0038E,U03C01C01EO0FCK04L03EO03IFE00306,U03C01C01EO0F8R03EV0303W03KF8,::U03C03C01EN01F8R01FV0303W03KF8,U03LFEN01FS0FFV03IF8X0E,U03LFEN01FQ07FFEV03IF8X0E,U03LFEN01FO03JFEgY0E,U03LFEN01FN07LFgY0E,gQ01FM01MFgY0E,gQ01FJ0F001MFS06gK0E,gQ01FI0FF803JFE1FS06gK0E,gQ01F001FFC03FFE001FS06gK0E,gQ01F003FFC03FJ01FS06gK0E,gH06N01F003FFCM01FS06gK0E,V01FE001EN01F003FFCM01FS06gK0E,V07FF803EO0F803EO01FS06J0CY01E,V0IFC0FEO0F8R01FS06J0CW03KF8,U01IFC3FEO0F8S0F8N03IFEJ0CW03KF8,U01F87EFF8O0F8S0F8N03IFEJ0CW03KF8,U03E01FFEP0F8S0F8W0C,U03C00FFCP0F8S0F8W0C,U03C00FFQ0F8S0F8W0C,U03C00FCQ0F8S0F8,U03C00FR0F8S0F8N030306gJ01F,U03C00FR0F8S0F8N030306gI01IF,U03C00FR0F8S0F8N030306gI07IFC,U03C00FR0F8S0F8N030306gI0JFE,U03C00FR0F8S0F8N030306gH01F003F,U03C00FR0F8S0F8N030306gH03CI0F8,U03C00FR07CR07F8N030306gH038I038,U03LFEO07CP03IF8N0387060038X038I038,U03LFEO07CN01KF8N03IFE003EX07J038,U03LFEO07CM0MF8N03IFE003F8P03FF8I07J038,U03LFEO07CK07NFV033EP0IFEI038I038,gR07CI07JF8001FFV030F8N01JFI038I078,gR07C03JFCK0FFV0303EN03F01F8003EI0F8,gR07DJFEM07F8P0F8I0300F8M078007C001F803F,gR07JFO03F8O03FFI030038M0FI03CI0JFE,gR07FF8P01FCO0IF8003I08M0FI01EI07IFC,W07CS03ES0FCO0E03C003Q0EJ0EI01IF,V03FFS03ES07CN01C01CT0EJ0E,V07FFCR03ES07EN01800ES01EJ0E,V0IFER03ES03EN038006S01EJ0E,U01IFER03ES03EN03I06T0EJ0E,U01F01FR03ES03FN03I06T0EJ0EL07,U03E00FR03ES01FN03I06T0FI01EI03C1FC,U03C0078Q03ES01FN03I06T07I03CI0FE3FE,U03C0078Q03ES01FN03IFET07C007C001FF3FF,U03C0078Q03ES01FN03IFE0030018M03F01F8003IF0F,U03C0078Q03ES01FN03IFE0030C18M01JFI03C3E078,U03C0078Q03ES01F8T030C18N0IFEI0381E038,U03C0078Q01FT0F8T030C18N03FF8I0700E038,U03C0078Q01FT0F8T030C18U0700C038,U03C0078Q01FO0FFC00F8T030C18U0700C038,U03C0078Q01FM07IFC00F8T030C18U0380C038,U03LFEO01FK03KFE00F8T03IF8M0EJ0E0038I038,U03LFEO01FI01MFC00F8T03IF8M0FJ0E003CI078,U03LFEO01F01OF800F8T03IF8M0FCI0E001F001F,U03LFEO01PFEJ0F8gL0FEI0EI0F001E,U03LFEO01OFL0F8N01FCU0FFI0EI07001C,gR01MF8M0F8N07FFL018M0EFC00EL01,gS0KFCO0F8N0F8F8K018M0E3E00E,gS0IFCP01F8M01E01CK018M0E1F80E,gR01FER01FN01800EK018M0E07C0E,gS0F8R01FN038006K018M0E03F0E,gS0F8R01FN03I06K018M0E00F8EI018018,gS0F8R03FN03I06K018M0E007EEI03C03C,gS0F8R03FN03I06003IF8M0E001FEI03C03C,gS0F8R03EN03800E003IF8M0EI0FEI03C03C,gS0F8R07EN01800ET0EI07EI018018,gS0F8R07EN01C01CT0EI01E,gS0F8R0FCO0FCF8T0EJ06,gS0F8R0FCO07FFL018,gS0F8Q01FEO01FCL018,gS0F8Q03FFX018V0180038,T01NF8O0F8Q0IF8W018N078006I0FF0038,S03OF83FFL0F8P03IF8W018M01FE01E001FF8038,S0PF87FFCK07CO07JF8M03IFEK018M03FF07E001FFC038,R01PF8IFCK07CM01LFCM03IFEK018M07FF8FE003C1E038,R01PF8E01CK07CL0LF87CM01IFE003IF8M0F87FF800380F838,R03PF8E00CK07CJ0LF8007CT03IF8M0F03FEI03807C38,R03PF8E01CK07C007KFK07CgL0E01F8I07003E38,R03PF8IFCK07C3KFM07CgL0E01EJ07001F38,R03PF87FFCK07JFEO07CN01FCU0E01CJ03800FB8,R03PF83FF8K07FFEQ07CN07FFL038M0E01CJ038007F8,R03FFCW07CS07CN0F878K0F8M0E01CJ03C003F8,R03FEO0EN07CS07CM01C01CJ07FN0E01CJ01F001F8,R01FCO0FN07CS07CM01800CI03F8N0E01CK0FI0F8,R01FCO07N07CS07CM01800E001FD8N0KFEI07I038,R01FCO0IFCK07CS07CM038006003E18N0KFE,S0FCO0IFCK07CS07CM03I06003818N0KFE,S0FCO0IFCK03ES07CM03I06003F18N0KFE,S0FEX03ES07CM03I06I0FD8,S07OF8O03ES03EM038006I01F8,R01PF87FF8K03ES03EM03IFEJ07FW03E0038,R03PF8IFCK03ES03EM03IFEK0F8R02I0FF0038,R03PF8IFCK03ES03EW018Q01E001FFC038,R03PF8E01CK03ES03EgP0FE003E7E038,R03PF8E00CK03ES0FEgO07FE00381F038,R03PF8600CK03EQ0IFEQ06I060CP01FF800380F838,R03PF8JFEJ03EN01KFEP03E001E0EP0FFCI07003C38,R03PF8JFEJ03EN07KFCO01FE003C07O07FF8I07001E38,R03PF8JFEJ03EJ02J03JFEO07FI030038M03FF38I03I0F38,gS03E003FCK01IFEN03FEI030018M0FF838I038007B8,gS03F7IFN0FFEM01FC6I030018M0FC038I038003F8,gS01JFCO0FEM03E06I030018M0F0038I03E001F8,gS01JFP03EM03C06I030018M0FE038I01FI0F8,gS01IFCP03EM03F86I030038M0FFC38J0FI078,gS01IF8P03FN07FEI01807N01FFB8J03I038,T03LFS0FFEQ01FN01FEI01F1FO07FF8,S01NFQ01FF8Q01FO03FCI0FFCP0FFC,S03NF8P01FER01FP07EI01FQ01FF8,S07NFCP01F8R01FQ0EW03FE,S0OFEP01FS01FM03gH07E,R01PFP01FS01FM03gH01E,R01PFP01FS01FM03gI02,R01PF8O01FS01FM03,R01PF8O01FL04L01FM03,R03PF8O01FK01F8K01FM038,R03FEL0FF8O01FK07FF8J01FM03IFET0KFE,R03FCL07F8O01FJ01JF8I01FM03IFE003Q0KFE,R03FCL07F8O01FJ03KFI01FM03L03CP0KFEL07,R03FCL07F8P0F8I0MF001FM03L01EP0FEM0701FC,R03FCL07F8P0F8003MFE01FM03M078O0FFM0F03FE,R03FCL07F8P0F8007NFE0F8L03M03EO03FEK01F07FF,R03FEL0FF8P0F801PFCF8L03N0FF8N07F8J03C07878,R03FF8J01FF8P0F807RF8U0FF8N01FEJ0380F078,R03PF8P0F81LFEI03FFU03CQ03FCI0380F038,R01PF8P0F83JFEK01FFP06J0FS0FFI0380E038,R01PFQ0F8JF8M07FN0E0F8003ES01FE00700E038,R01PFQ0FBFFEO03FM01E1FC0038T07E00701E038,S0PFQ0JFP03F8L01C38E002U07E00381C038,S0OFEQ0IFCP01F8L038306W01FE00381C038,S07NFCQ0IF8Q0FCL030306W0FFI03C3C078,S01NF8Q07FFR0FCL030706V03FCI01FF80F8,T07LFCR07FER07CL030606U01FEJ01FF81F,U03JF8S03FCR07EL038606U07F8K0FF01E,gT01F8R07EL018E0ET03FCL03E01C,gT01F8R03EL01FC1CT0FF,gT01FS03EM0F83CT0KFE,gT01FS03EO03J07FCN0KFE,gT03FS01FS01FFEN0KFEM018,O07FE1PF8P03ES01FS03C07N0KFEM078,O07FE3PF8P03ES01FS030038Y03F8,O07FE3PF8P03ES01FL0303060030018X01FF8,O07FE3PF8P03EP07001FL0303060020018X0FFC,O07FE3PF8P03EN03FFC01FL0303060030018W07FF,O07FE3PF8P03EL03JFC01FL0303060038038V01FFE,O07FE3PF8P07EJ01LFC01FL030306001E1FW0FFCE,O07FE3PF8P07CI0NFC01FL030306I0FFEV03FE0E,O07FE3PF8P07C007NF801FL030306I03F8V03F00E,O07FE3PF8P07C00NFI01FL03870EgH03C00E,gT07C00LF8J01FL03IFEgH03F80E,gT07E00JFCL01FL03IFEK0CV03FF0E,gT03E00FFEN01FV0CW07FEE,gT03ES01FS03IF8W0FFE,gT03ES01FS03IF8M0EJ0EJ01FF8,gT03ES03FS0380CN0E0380EK07FF,T03FI0FFER03ES03ES03C0CN0E0380EL0FF8,T0FF001IF8Q03ES03ET0E0CN0E0380EL01F8,S03FF007IFCQ03ES03ET078CN0E0380EM038,S07FF00JFEQ03FS07ET01ECN0E0380E,S0IF00JFEQ01FS07EU07CN0E0380E,S0IF01KFQ01FS0FFN0F8K03CN0E0380E,R01IF03KFQ01F8R0FFM03FFL0CN0E0380E,R01IF03KF8Q0F8Q01FF8L0IF8T0E0380EM038,R01IF07KF8Q0FCQ03FF8K01E03CT0E0380EM038,R03FF007FF0FF8Q0FCQ07FF8K01C01CT0KFEM038,R03FC00FFC07F8Q07EP01F8F8K03800ET0KFEM038,R03FC00FFC07F8Q07FS0F8K038006T0KFEM038,R03FC01FF803F8Q03FO03800F8K03I06gM038,R03FC01FF003F8Q03F8M0FF800F8K03I06gM038,R03FC03FF003F8Q01FEK07IF800F8K038006L08g038,R03FC07FE007F8Q01FF8007KF800F8K01800EK078g038,R03FE0FFE00FF8R0QF800F8K01C01CJ03F8g038,R03KFC01FF8R07PF800F8L0F07CI01FCO01FFO038,R01KFC1IF8R03PF8007CL07FF8I0FF8O07FFCI03KF8,R01KF81IF8R01PFC007CL03FEI03F18N01IFEI03KF8,R01KF81IFT0PFC007CR03818N03F83FI03KF8,S0KF01IFT03OFC007CR03C18N07C0078,S0JFE01FFES01PFC007CK03L01F98N078003C,S07IFC01FFCR03QFI07CK03M03F8N0FI01C,S03IF801FF8O0SF8J07CK03N0FEN0EI01E,T0IF001FEN01RFCL07CK03N01F8M0EJ0E,T01F8001EO03PFEN07CK03O038M0EJ0E,gP07OFP07CK03IFE003Q0EJ0E,gP07MF8Q07CK03IFE003Q0EJ0E,gP07KF8S07CK03IFE003Q0EJ0E,gP0IFC0FCS07CK03L03Q0EJ0E,g0FO0FEJ0FS03EK03L03Q0EJ0E,T03FI0FFEN0F8J038R03EK03L03IF8M0KFE,S01FF003IF8M0F8J01CR03EK03L03IF8M0KFE,S03FF007IFCM07CK0ER03EK03L03Q0KFE0038I038,S07FF00JFEM07CK07R03E006O03Q0KFE00380E038,S0IF01KFM07CK038Q03E3FF8N03Y0380E038,S0IF01KFM07CK018Q03JFCN03Y0380E038,R01IF03KFM07CK01CQ03JFC03IFE003Y0380E038,R01IF03KF8L07CL0CQ03JFE03IFEgH0380E038,R01IF07KF8L07CL0EQ03JFEJ03CgH0380E038,R03FE007FE0FF8L07CL06P03KFEJ078gH0380E038,R03FC00FFC07F8L07CL07N01LF3EJ0FI03FFEV0380E038,R03FC00FF807F8L07CL03M0LF803EI03CI03IFV0380E038,R03FC01FF803F8L07CI01803K07MF003EI078L07V0380E038,R03FC03FF003F8L07CJ0E038003OF003E001EM018U03KF8,R03FC03FF003F8L07CJ0E0181QF803E003CM018U03KF8,R03FC07FE007F8L07EJ0F01LFJ07FF803F00FN018U03KF8,R03FE0FFE00FF8L03EJ0MF8K01FF801F01EN018Q0CI03KF8,R03KFC03FF8L03EI07KFCN0FF801F03IFEK018Q0C,R01KFC1IF8L03E07KFEP07F801F03IFEK038Q0C,R01KF81IF8L03MFR07F801FQ0FN0KFE,R01KF81IFM03KFES03F801FN03FFEN0KFE,S0KF01IFM03KFT01F801FN03FFCN0KFEJ03F,S0JFE01FFEM03JF8T01F801Fg0KFEI01FFE,S07IFC01FFCM01JFV0F801F030306T0F800CJ03IF8,S03IF801FF8N0IFEV0F801F030306L08M07C00CJ07IFC,T0IF001FEO07FFCV0F801F030306I0F838M03F00CJ0F803E,U0FI018Q03F8V07801F030306001FC78N0F80CI01E001F,gR07FW07801F0303060038FFO03E0CI01CI0F,gR07EW07001F03030600307CO01F8CI03CI07,gR0FCgG0F83030600307Q07CCI038I038,gR0FCgG0F83870600306Q03FCI038I038,gQ01F8gG0F83IFE00306R0FCI038I038,T07IFC3FFO01F8gG0F83IFE00306R07CI038I038,S01JFC3FF8N01FgH0F8M03IF8P01CI038I038,S07JFC3FFEN03FgH0F8M03IF8U038I038,S0KFC3FFEN03FgH0F8gM038I038,S0KFC3IFN03EgH0F8gM038I078,R01KFC3IFN03EgH0F83IFEgH03KF8,R01KFC3IF8M07EgH0F83IFEgH03KF8,R01KFC3IF8M07CgH0F8gM03KF8,R03KFC3IF8M07CgH0F8Y0KFE,R03FE00FC00FF8M07CgH0F8Y0KFE,R03FC00FC007F8M07CT01F8K0F8Y0KFE,R03FC00FC003F8M07CS0IFCJ01F83IFET0F,R03F800FC003F8M07CQ07JFEJ01F03IFET07,R03F800FC003F8M0F8O07LFEJ01F03FW078,R03FC00FC003F8M0F8M03NFEJ03F01F8V038,R03FC00FC007F8M0F8K01PFEJ07F003FV01C,R03FE00FC00FF8M0F8J0RFEI03FEI0FCU01E,R03PF8M0F8I07RFC001FFEI01F8,R03PF8M0F8I0RFEI01FFCJ07E,R01PF8M0F8001QF8J01FF8J01E,R01PF8M0F8001OFCL01FFK07EgN08,R01PFN0F8001MFEN01FEJ01F8gI03F8038,S0PFN0F8001LFP01F8J0FCgJ0FFC0F8,S07NFEN0F8001JFR01FJ03FgJ01FFE3F8,S03NFCN0F8I0FF8S01FI01F8gJ01E1E7F,S01NF8N0F8Y01FI03FgK03C0FFC,T03LFCO07CY01FI03IFEgH03807F,gQ07CY01FI03IFEgH03807C,gQ07CY03FgP038078,gQ07CY03EgI01FFJ03807,gQ07CY03EgI07FFCI03807,gQ07CY03EM0ET01IFEI03807,Y01F8O07EY07EL07ET03FC7F8003807,T07FI0IFO03EY07CK03FCT07E00F80038078,S01FF003IF8N03EY07CK0FEU078003C003KF8,S03FF007IFCN03EY0FCJ07FEU0FI01E003KF8,S07FF00JFEN03FY0F8I03F86U0EJ0E003KF8,S0IF01KFN01FX01F8I03C06U0EJ0E,S0IF01KFN01FX03F8I03E06T01EJ0E,R01IF03KFN01F8W03FJ01FC6T01EJ0E,R01IF03KF8N0F8W07FK03FET01EJ0E,R01FFC07KF8N0FCW0FEL0FFU0EJ0E,R03FE007FE0FF8N0FCV01FCL01FET0EI01E003KF8,R03FC00FFC07F8N07EV03FCM03ET0FI01E003KF8,R03FC00FF803F8N07FV0FF8N06T078003C003KF8,R03FC01FF803F8N03FU03FFgJ07C00FC003KF8,R03FC03FF003F8N03F8S01FFEgJ03FEFF8,R03FC03FF003F8N01FCR01IFCgJ01IFE,R03FC07FE007F8N01FEQ07JF8K03IFEU07FFC,R03FE1FFE00FF8O0FF8N03LFL03IFEV0FE,R03KFC03FF8O07FCL01MFCO03CgH038,R01KFC1IF8O03FFK0OFP078gH038,R01KF81IF8O01FFE00PFCP0EgI038,R01KF81IFQ0TFEP03CgI038,S0KF01IFQ07RFCQ078V03FF8I038,S07IFE01FFEQ03QFR01EW0IFEI038,S07IFC01FFCR0OF8S03CV01JFI038,S01IF801FF8R07LFCU0FW03F01F8003KF8,T0FFE001FCT0JFEV01EW07C0078003KF8,U06hG03IFET078003C003KF8,hW03IFET0FI01C0038,iV0EI01E0038,iV0EJ0E0038,:hY07V0EJ0E0038,hX03FEU0EJ0E0038,hX0IF8T0EJ0E0038,hX0E03CT0EJ0E003,hW01C01CT0EJ0E,hW03800ET0KFE,O03hM038006T0KFEN08,O03hM03I06T0KFEI03F0038,O038hL03I06gI0FFC0F8,O03ChL038006gH01FFE1F8,O07ChL01800EgH01F3E7F,O07EhL01C01CgH03C0FFC,O07EhM0F03CY06003807F,O07FhM0IF8X03E003807C,O0FF8L07F8gW03FEX01FE0038078,O0FF8K01FFEhX0FFE003807,O0FFCK07IF8hV03FFI03807,O0FFCK0JFEhU01FF8I03807,O0FFEJ01KFhU0IF8I03807,O07FFJ03KF8gT03IFET07FE38I038078,O03FFJ07KFCgT03IFET0FF038I03KF8,O03FF8I0LFEhS0F8038I03KF8,O01FF8001LFEhS0F8038I03KF8,O01FFC001MFhS0FF038,P0FFC003FFE0IF8gT0302U07FE38,P07FE003FFC03FF8gT07038U0IF8,P07FF003FF801FFCgT0F03CU03FF8,P03FF003FFI0FFCgS01C01CV07FEN038,N0803FF801FEI07FEgS03800EW0FFCM0F8,N0801FF801FCI07FEgS038006W01FEL07F8,N0C01FFC00FCI03FFgS03I06X03EK03FF,M01C00FFE0078I03FF8gR03I06Y0EJ01FFC,M01E007FE0078I01FF8gR038006gJ07FE,M01F007FF003K0FFCgR03800EgI03FFE,M01F003FF003K0FFCgR01C00CgH01FF8E,M03F803FF801004007FEgR01E03CW03FI03FC0E,M03F801FFCJ0E007FEgS0IF8T01F87F8003E00E,M03FC00FFCJ0E003FFgS03FFU03FCFFC003E00E,M03FC00FFEI01F003FFgT02V07FDF3C003FC0E,M07FE007FEI03F001FF8hP0F9FC1E001FF8E,M07FE007FFI03F801FF8gU06T0F0780EI03FFE,M03FF003FF8007F800FFCgT03ET0E0780EJ07FE,M03FF801FF8007FC00FFEgS01FET0E0300EK0FFC,M01FF801FFC00FFE007FEgS0FFU0E0300EK03FF8,N0FFC00FFC007FE003FFgR03FEU0E0300EL07F8,N0FFC00FFE007FF003FFgQ01FC6U0E0300EM0F8,N07FE007FF003FF001FF8gP03E06U0E0300EM018,N07FE003FF001FF801FF8gP03C06U0E0300EI01E,N03FF003FF801FFC00FFCgP01F86U0KFEI07FC,N03FF001FF800FFC00FFCgQ07FEU0KFEI0FFE,N01FF801FFC00FFE007FEgR0FEU0KFE001IF,N01FFC00FFE007FE007FFgR03FCT0KFE003E0F,O0FFC007FC007FF003FFgS07EgH03C07,O07FE007FC003FF801FF8gS0EgH038038,O07FE003F8001FF801FF8gO03gL038038,O03FF003FI01FFC00FFCgO03gI020038038,O03FF001FJ0FFC00FF8gO03gH01E0038038,O01FF801EJ0FFE007F8gO03gH0FE0038038,O01FF800EJ07FF007F8gO03gG07FE0038038,P0FFC00401003FF003F8gO038Y01FF80038038,P0FFCJ01003FF803FgP03IFEV0FFCI03KF8,P07FEJ03801FF801FgP03IFER08007FF8I03KF8,P03FFJ03801FFC00FgP03V0E03FF38I03KF8,P03FFJ03C00FFC00EgP03V0F0FF838I03KF8,P01FF8I07E007FE006gP03V078FC038,P01FF8I07E007FF006gP03V018F0038,Q0FFCI0FF003FF002gP03X0FE038,Q0FFC001FF003FF8hQ0FFC38,Q07FE001FF801FF8hQ01FFB8,Q07FF003FF800FFCgU06V07FF8,Q03FF80IF800FFEgS0E0F8V0FFC,Q01FFE1IFI07FEgR01E1FCV01FF8,Q01MFI07FFgR01C38EW03FE,R0LFEI03FFgR038306X07E,R07KFCI01FF8gQ030306X01E,R07KFCI01FFCgQ030706Y02,R03KF8J0FFCgQ030606gM038,S0KFK0FFEgQ038606gL01F8,S07IFCK07FEgQ018E0EU0403EM07F8,S01IF8K07FEgQ01FC1CT01C07F8K03FF,T07FEL03FEgR0F83CT07C0FFCJ01FF8,gI01FCgT01U07C1F7CJ0FFE,gI01FChP0F01E1EI07FFE,gJ0FChP0E03C0E001FF0E,gJ0FCgU06T0E0380E003FC0E,gJ078gQ030306S01C0380E003E00E,gJ038gQ030306S01C0380E003F00E,gJ038gQ030306S01C0780E003FE0E,gJ018gQ030306T0E0700EI0FFCE,gJ01gR030306T0E0700EI03FFE,hW030306T0F0F01EJ07FE,hW030306T07FE03CK0FFC,hW03870ET07FE07CK01FF8,hW03IFET03FC078L07F8,hW03IFEU0F007N0F8,jO018,,::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::^FS

^FO320,140
^A0B,22,28
^FDINVITACION^FS

^FO345,140
^A0B,30,35
^FDGENERAL^FS

^FO150,350
^A0B,32,38
^FDINVITACION^FS

^FO190,330
^A0B,50,55
^FDGENERAL^FS

^FO100,90
^BQB,2,7
^FDLA,<qr_code>^FS

^FO290,140
^A0B,20,30
^FD<order_id>^FS

^FO370,230
^A0B,16,16
^FD<number>^FS



 ^XZ
`;

/**
 * PhysicalTicketsTemplate - Generate ZPL batch for physical ticket printing
 *
 * Theme Support: Full light/dark mode support
 * Colors Used: bg-surface-base, text-text-primary, border-border-primary, bg-surface-secondary
 */
const PhysicalTicketsTemplate: React.FC = () => {
  const { event, eventLoading } = useGetSelectedEvent();
  const { eventId, businessId } = useGetParams();

  // Fetch tickets for the event
  const { data: tickets, isFetching: ticketsFetching } = useGetAllTicketsQuery(
    {
      businessId,
      eventId,
      publicSaleAvailable: false,
    },
    { skip: !event },
  );

  // Fetch items export data
  const { data: itemsExport, isFetching: itemsFetching } = useGetEventItemsExportQuery(
    { eventId },
    { skip: !event },
  );

  const [selectedTicketId, setSelectedTicketId] = useState<string>('');
  const [template, setTemplate] = useState<string>(DEFAULT_ZPL_TEMPLATE);
  const [generatedZpl, setGeneratedZpl] = useState<string>('');
  const [copied, setCopied] = useState<boolean>(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [uploadedImageName, setUploadedImageName] = useState<string | null>(null);
  const [imageProcessing, setImageProcessing] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Filter items by selected ticket
  const filteredItems = useMemo(() => {
    if (!itemsExport || !selectedTicketId) {
      return [];
    }
    return itemsExport.filter((item) => item.item_ticket_id === selectedTicketId);
  }, [itemsExport, selectedTicketId]);

  // Generate ZPL for a single ticket
  const generateZplForTicket = (templateStr: string, item: ItemExportRow, index: number): string => {
    // Split name by space to get first name and last name
    const nameParts = (item.name || '').trim().split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || item.last_name || '';

    let zpl = templateStr;
    zpl = zpl.replace(/<name\[0\]>/g, firstName);
    zpl = zpl.replace(/<name\[1\]>/g, lastName);
    zpl = zpl.replace(/<qr_code>/g, item.qr_code || '');
    zpl = zpl.replace(/<order_id>/g, item.order_display_id || '');
    zpl = zpl.replace(/<number>/g, String(index + 1).padStart(5, '0'));
    return zpl;
  };

  // Generate batch ZPL
  const handleGenerate = () => {
    if (!filteredItems.length) {
      return;
    }

    const allZpl = filteredItems
      .map((item, index) => generateZplForTicket(template, item, index))
      .join('');

    setGeneratedZpl(allZpl);
    setCopied(false);
  };

  // Copy to clipboard
  const handleCopy = async () => {
    if (!generatedZpl) {
      return;
    }
    try {
      await navigator.clipboard.writeText(generatedZpl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Download ZPL file
  const handleDownload = () => {
    if (!generatedZpl) {
      return;
    }

    // Get ticket name from selected ticket
    const selectedTicket = tickets?.find((t) => t.id === selectedTicketId);
    const ticketName = selectedTicket?.name || 'ticket';

    // Sanitize names for filename (remove special characters)
    const sanitize = (str: string) => str.replace(/[^a-zA-Z0-9-_]/g, '_');

    const eventName = sanitize(event?.name || 'event');
    const sanitizedTicketName = sanitize(ticketName);
    const amount = filteredItems.length;

    const filename = `${eventName}-${sanitizedTicketName}-${amount}.zpl`;

    // Create blob and download
    const blob = new Blob([generatedZpl], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Preview ZPL using Labelary API
  const handlePreview = async () => {
    // Generate ZPL for first item only (for preview)
    let zplToPreview = template;

    if (filteredItems.length > 0) {
      // Use first item data for preview
      zplToPreview = generateZplForTicket(template, filteredItems[0], 0);
    } else {
      // Use placeholder data for preview if no items
      zplToPreview = template
        .replace(/<name\[0\]>/g, 'NOMBRE')
        .replace(/<name\[1\]>/g, 'APELLIDO')
        .replace(/<qr_code>/g, 'PREVIEW_QR_CODE_12345')
        .replace(/<order_id>/g, 'ABC123')
        .replace(/<number>/g, '00001');
    }

    setPreviewLoading(true);
    setPreviewError(null);

    try {
      // Create form data for Labelary API
      const formData = new FormData();
      const blob = new Blob([zplToPreview], { type: 'text/plain' });
      formData.append('file', blob, 'label.zpl');

      const response = await fetch(LABELARY_API_URL, {
        method: 'POST',
        headers: {
          Accept: 'image/png',
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Error de Labelary API: ${response.status}`);
      }

      const imageBlob = await response.blob();
      const imageUrl = URL.createObjectURL(imageBlob);

      // Revoke previous URL to prevent memory leaks
      if (previewImageUrl) {
        URL.revokeObjectURL(previewImageUrl);
      }

      setPreviewImageUrl(imageUrl);
    } catch (err) {
      console.error('Failed to generate preview:', err);
      setPreviewError(err instanceof Error ? err.message : 'Error al generar vista previa');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Handle image upload and convert to ZPL
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    setImageProcessing(true);
    setUploadedImageName(file.name);

    try {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (readerEvent) => {
        img.onload = () => {
          // Create canvas to get image data
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            setImageProcessing(false);
            return;
          }

          // Set canvas size to image size (you can resize here if needed)
          canvas.width = img.width;
          canvas.height = img.height;

          // Draw image to canvas
          ctx.drawImage(img, 0, 0);

          // Get image data
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

          // Convert to ZPL GFA format (positioned at x=10, y=50 like the original)
          const zplGfa = imageToZplGfa(imageData, 10, 50);

          // Replace GFA in template
          const newTemplate = replaceGfaInTemplate(template, zplGfa);
          setTemplate(newTemplate);

          setImageProcessing(false);
        };

        img.src = readerEvent.target?.result as string;
      };

      reader.readAsDataURL(file);
    } catch (err) {
      console.error('Error processing image:', err);
      setImageProcessing(false);
    }

    // Reset file input so same file can be uploaded again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const ticketOptions = useMemo(() => {
    if (!tickets) {
      return [];
    }
    return tickets.map((ticket) => ({
      value: ticket.id,
      label: ticket.name,
    }));
  }, [tickets]);

  const isLoading = eventLoading || ticketsFetching || itemsFetching;

  return (
    <div className="flex flex-col gap-8 w-full my-6">
      <AdminSectionTitle
        title="Eventos"
        subtitle={`${event?.name || ''} - Imprimir Entradas Físicas`}
      />

      <div className="glass-surface flex flex-col gap-6">
        {/* Ticket Type Selection */}
        <div className="flex flex-col gap-2">
          <label className="text-text-primary font-medium">Tipo de Entrada</label>
          <Select
            id="ticket-type-select"
            options={ticketOptions}
            value={selectedTicketId}
            onChange={(e) => setSelectedTicketId(String(e.target.value))}
            placeholder="Seleccionar tipo de entrada..."
          />
          {selectedTicketId && (
            <p className="text-text-secondary text-sm">
              {filteredItems.length} entrada(s) encontrada(s) para este tipo
            </p>
          )}
        </div>

        {/* Image Upload */}
        <div className="flex flex-col gap-2">
          <label className="text-text-primary font-medium">
            Imagen del Template
          </label>
          <p className="text-text-tertiary text-sm">
            Sube una imagen para reemplazar el gráfico del template. La imagen se convertirá automáticamente a formato ZPL.
          </p>
          <div className="flex flex-row gap-4 items-center">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
              id="image-upload"
            />
            <Button
              variant="secondary"
              onClick={() => fileInputRef.current?.click()}
              disabled={imageProcessing}
              loading={imageProcessing}
              icon={<FiUpload />}
            >
              {imageProcessing ? 'Procesando...' : 'Subir Imagen'}
            </Button>
            {uploadedImageName && (
              <span className="text-text-secondary text-sm">
                Imagen cargada: {uploadedImageName}
              </span>
            )}
          </div>
        </div>

        {/* Template Input */}
        <div className="flex flex-col gap-2">
          <label className="text-text-primary font-medium">
            Template ZPL
          </label>
          <p className="text-text-tertiary text-sm">
            Placeholders disponibles: {' '}
            <code className="bg-surface-secondary px-1 rounded">&lt;name[0]&gt;</code>,{' '}
            <code className="bg-surface-secondary px-1 rounded">&lt;name[1]&gt;</code>,{' '}
            <code className="bg-surface-secondary px-1 rounded">&lt;qr_code&gt;</code>,{' '}
            <code className="bg-surface-secondary px-1 rounded">&lt;order_id&gt;</code>,{' '}
            <code className="bg-surface-secondary px-1 rounded">&lt;number&gt;</code>
          </p>
          <textarea
            className="w-full h-48 p-3 bg-surface-base border border-border-primary rounded-xl text-text-primary font-mono text-sm resize-y focus:outline-none focus:ring-1 focus:ring-primary-500/20"
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
            placeholder="Ingrese el template ZPL..."
          />
        </div>

        {/* Action Buttons */}
        <div className="flex flex-row gap-4 flex-wrap">
          <Button
            onClick={handleGenerate}
            disabled={!selectedTicketId || !template || isLoading || filteredItems.length === 0}
            loading={isLoading}
          >
            Generar ZPL ({filteredItems.length} entradas)
          </Button>
          <Button
            variant="secondary"
            onClick={handlePreview}
            disabled={!template || previewLoading}
            loading={previewLoading}
            icon={<FiEye />}
          >
            Vista Previa
          </Button>
        </div>

        {/* Preview Section */}
        {(previewImageUrl || previewLoading || previewError) && (
          <div className="flex flex-col gap-2">
            <label className="text-text-primary font-medium">
              Vista Previa de Etiqueta
            </label>
            {previewError && (
              <div className="p-4 bg-error-100 border border-error-300 rounded-xl text-error-600">
                {previewError}
              </div>
            )}
            {previewImageUrl && !previewError && (
              <div className="p-4 bg-surface-secondary border border-border-primary rounded-xl">
                {/* Container accounts for rotated dimensions: label is tall/narrow, rotated becomes wide/short */}
                <div
                  className="mx-auto flex items-center justify-center"
                  style={{
                    width: 'min(100%, 700px)',
                    height: '280px',
                  }}
                >
                  <img
                    src={previewImageUrl}
                    alt="Vista previa de etiqueta ZPL"
                    className="border border-border-secondary rounded-lg shadow-sm"
                    style={{
                      transform: 'rotate(90deg)',
                      width: '280px',
                      height: 'auto',
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Generated Output */}
        {generatedZpl && (
          <div className="flex flex-col gap-2">
            <div className="flex flex-row justify-between items-center">
              <label className="text-text-primary font-medium">
                ZPL Generado
              </label>
              <div className="flex flex-row gap-2">
                <Button
                  variant="secondary"
                  onClick={handleDownload}
                  icon={<FiDownload />}
                >
                  Descargar
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleCopy}
                  icon={copied ? <FiCheck className="text-success-600" /> : <FiCopy />}
                >
                  {copied ? 'Copiado!' : 'Copiar'}
                </Button>
              </div>
            </div>
            <textarea
              className="w-full h-64 p-3 bg-surface-secondary border border-border-primary rounded-xl text-text-primary font-mono text-xs resize-y focus:outline-none"
              value={generatedZpl}
              readOnly
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PhysicalTicketsTemplate;
